import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack } from 'expo-router';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * Terms and Conditions Screen
 * Displays the terms of service for using the FimFam-AI app.
 * Accessible from the settings screen.
 */
export default function TermsScreen() {
  const colorScheme = useColorScheme();
  const backgroundColor = Colors[colorScheme ?? 'light'].background;
  const textColor = Colors[colorScheme ?? 'light'].text;
  const accentColor = Colors[colorScheme ?? 'light'].tint;

  return (
    <>
      <Stack.Screen
        options={{
          title: 'Terms & Conditions',
          headerShown: true,
        }}
      />
      <ScrollView
        style={[styles.container, { backgroundColor }]}
        contentContainerStyle={styles.content}>
        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>1. Acceptance of Terms</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            By accessing and using FimFam-AI, you accept and agree to be bound by the terms and
            provision of this agreement. If you do not agree to these terms, please do not use this
            application.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>2. Use License</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            Permission is granted to use FimFam-AI for personal, non-commercial purposes. This
            license shall automatically terminate if you violate any of these restrictions and may
            be terminated at any time.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>3. Privacy and Data</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            FimFam-AI processes calendar and family information to provide AI-assisted coordination
            services. Your data is handled in accordance with our privacy practices. We use
            third-party services including Supabase for authentication and OpenAI for AI
            functionality.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>4. User Accounts</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            You are responsible for maintaining the confidentiality of your account credentials and
            for all activities that occur under your account. You agree to notify us immediately of
            any unauthorized use.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>5. AI-Generated Content</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            Content generated by the AI assistant is provided &quot;as is&quot; without warranties of any
            kind. You acknowledge that AI can make errors and agree to verify all information before
            relying on it.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>6. Prohibited Uses</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            You may not use FimFam-AI for any unlawful purpose, to harass others, to violate
            intellectual property rights, or to transmit malicious code or harmful content.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>7. Service Modifications</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            We reserve the right to modify or discontinue the service at any time without notice. We
            shall not be liable to you or any third party for any modification, suspension, or
            discontinuance of the service.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>8. Limitation of Liability</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            In no event shall FimFam-AI or its developers be liable for any damages arising out of
            the use or inability to use the application, even if advised of the possibility of such
            damages.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>9. Governing Law</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            These terms shall be governed by and construed in accordance with applicable laws,
            without regard to its conflict of law provisions.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>10. Changes to Terms</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            We reserve the right to update these terms at any time. Continued use of the application
            after changes constitutes acceptance of the modified terms.
          </Text>
        </View>

        <View style={[styles.footer, { borderTopColor: '#e0e0e0' }]}>
          <Text style={[styles.footerText, { color: textColor }]}>
            Last Updated: May 2026
          </Text>
          <Text style={[styles.footerText, { color: textColor }]}>FimFam-AI</Text>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  section: {
    marginBottom: 24,
  },
  heading: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.85,
  },
  footer: {
    marginTop: 32,
    paddingTop: 20,
    borderTopWidth: 1,
    gap: 8,
  },
  footerText: {
    fontSize: 12,
    opacity: 0.5,
    textAlign: 'center',
  },
});
